import { parseAdvancingMoney } from "advancing/utils";
import { RadioGroup } from "components/Atoms/inputs/RadioGroup";
import CurrencyInput from "components/CurrencyInput";
import CustomOptionDropdown from "components/dropdowns/reactselect/CustomOptionDropdown";
import CountryStateInput from "components/formcomponents/countrystateinput";
import FFInPageEditOptionalDatePicker from "components/Freefields/InPageEdit/FFInPageEditOptionalDatePicker";
import FFInPageEditOptionalDateTimePicker from "components/Freefields/InPageEdit/FFInPageEditOptionalDateTimePicker";
import OptionalTimePicker from "components/OptionalTimePicker";
import PercentageInput from "components/PercentageInput";
import UrlInput from "components/urlinput";
import OptionalNumberInput from "components/ValidatedFields/OptionalNumberInput";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import EmailInput from "components/ValidatedFields/WithErrorLabel/EmailInput";
import moment from "moment";
import React from "react";
import { parseFreeFieldPercentage, toFreeFieldPercentageValue } from "shared/utils/freeFields/utils";
import { parseLocalizedNumber } from "shared/utils/numberInputUtils";
import { numberDecimalSeparator } from "shared/utils/numberUtils";

interface Props {
  type: server.dto.FormElementTypes
  dropdownOptions: server.dto.DropdownOption[]
  maxLength: number
  reference: string
  value: string
  // The value as the server sent it, some types read extra context from it
  serverValue: string
  onChange: (value: string, submit?: boolean) => void
  onEnter: (value: string) => void
  cancel: () => void
}

export const AdvancingValueInput = (props: Props) => {
  const { type } = props;
  const locale = serverReference.culture.uiLocale;

  const toNumberValue = (value: number) => {
    const str = value?.toString();
    return !str || str === "NaN" ? "" : str;
  }

  // The server formats decimals in the user's culture
  const toDecimalValue = (value: number) => toNumberValue(value).replace(".", numberDecimalSeparator(locale));

  switch (type) {
    case "boolean":
      return <div style={{ paddingTop: 6, paddingLeft: 8 }}>
        <RadioGroup
          orientation="horizontal"
          options={[
            { label: RESX.GeneralLabels.lblYes, value: "Yes" },
            { label: RESX.GeneralLabels.lblNo, value: "No" }
          ]}
          value={props.value}
          onChange={v => props.onChange(v, true)}
        />
      </div>;
    case "booleanNullable":
      return <div style={{ paddingTop: 6, paddingLeft: 8 }}>
        <RadioGroup
          orientation="horizontal"
          options={[
            { label: RESX.GeneralLabels.lblYes, value: "Yes" },
            { label: RESX.GeneralLabels.lblNo, value: "No" },
            { label: RESX.GeneralLabels.NoChoiceMade, value: "" }
          ]}
          value={props.value}
          onChange={v => props.onChange(v, true)}
        />
      </div>;
    case "date":
      return <FFInPageEditOptionalDatePicker
        value={props.value}
        onChange={props.onChange}
        reference={props.reference}
        submit={() => props.onEnter(props.value)}
        cancel={props.cancel}
      />
    case "dateTime":
      return <FFInPageEditOptionalDateTimePicker
        value={props.value}
        onChange={props.onChange}
        reference={props.reference}
        submit={() => props.onEnter(props.value)}
        cancel={props.cancel}
      />
    // The server sends times as a full ISO date time
    case "time":
      return <OptionalTimePicker
        autoFocus
        value={props.value ? moment.utc(props.value) : undefined}
        onChange={(v, submit) => props.onChange(v ? moment.utc(v.format("HH:mm"), "HH:mm").toISOString() : "", submit)}
      />;
    case "decimal":
      return <OptionalNumberInput
        canBeNegative
        canBeEmpty
        autoFocus
        width={140}
        value={parseLocalizedNumber(props.value, locale)}
        onChange={(v: number) => props.onChange(toDecimalValue(v))}
        onEnterPressed={(v: number) => props.onEnter(toDecimalValue(v))}
        onEscPressed={() => props.cancel()}
        decimalPoints={2}
      />;
    case "integer":
    case "integerNullable":
      return <OptionalNumberInput
        autoFocus
        canBeNegative
        canBeEmpty
        width={140}
        value={parseFloat(props.value) || 0}
        onChange={(v: number) => props.onChange(toNumberValue(v))}
        onEnterPressed={(v: number) => props.onEnter(toNumberValue(v))}
        onEscPressed={() => props.cancel()}
        decimalPoints={0}
      />;
    case "capacity":
      return <OptionalNumberInput
        autoFocus
        canBeEmpty
        width={100}
        minValue={0}
        maxValue={serverReference.constants.venue.maxCapacity}
        rightLabel="PAX"
        value={parseFloat(props.value) || 0}
        onChange={(v: number) => props.onChange(toNumberValue(v))}
        onEnterPressed={(v: number) => props.onEnter(toNumberValue(v))}
        onEscPressed={() => props.cancel()}
        decimalPoints={0}
      />;
    case "money":
      return <CurrencyInput
        focusOnMount
        canBeEmpty={false}
        canBeNegative={false}
        currency={parseAdvancingMoney(props.serverValue).currency}
        value={parseFloat(props.value) || 0}
        onChange={v => props.onChange(toNumberValue(v))}
        onEnter={v => props.onEnter(toNumberValue(v))}
        onEsc={props.cancel}
      />;
    case "percentage":
      return <PercentageInput
        autoFocus
        onEnter={v => props.onEnter(toNumberValue(toFreeFieldPercentageValue(v)))}
        onEsc={props.cancel}
        canBeNegative={false}
        digitsAfterDecimalPoint={2}
        enablePercentageLabel
        value={parseFreeFieldPercentage(props.value) || 0}
        onChange={v => props.onChange(toNumberValue(toFreeFieldPercentageValue(v)))}
      />;
    case "dropdown":
      return <CustomOptionDropdown
        excludeKeys={[]}
        options={props.dropdownOptions}
        value={props.dropdownOptions.find(option => option.key === props.value)}
        onChange={v => props.onChange(v.key, true)}
        onEsc={() => props.cancel()}
        config={{
          focusOnMount: true,
          openMenuOnFocus: true,
        }}
      />;
    case "email":
      return <EmailInput
        focusOnMount
        validatorKey={`advancing${props.reference}`}
        value={props.value}
        onChange={props.onChange}
        onEnter={() => props.onEnter(props.value)}
        onEsc={() => props.cancel()}
        emailCanBeBlank
      />;
    case "textMultiLine":
      return <OptionalTextAreaInput
        focusOnMount
        maxLength={props.maxLength}
        value={props.value}
        onChange={(v) => { props.onChange(v); }}
        onCtrlEnter={() => props.onEnter(props.value)}
        onEsc={() => props.cancel()}
      />;
    case "textSingleLine":
      return <OptionalTextInput
        focusOnMount
        maxLength={props.maxLength}
        value={props.value}
        onChange={(v) => { props.onChange(v); }}
        onEnter={() => props.onEnter(props.value)}
        onEsc={() => props.cancel()}
      />;
    case "url":
      return <UrlInput
        focusOnMount
        value={props.value}
        onValueChange={props.onChange}
        validatorKey={`advancing${props.reference}`}
        onEnter={() => props.onEnter(props.value)}
        onEsc={props.cancel}
        maxLength={serverReference.constants.url.maxUrlLength}
      />;
    case "countryState":
      return <CountryStateInput
        value={props.value}
        onChange={props.onChange}
      />;
    default:
      throw new Error("Unknown advancing form element type: " + type);
  }
};
